import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  getImportedListingsForUser,
  importListingFromUrl as importListingFromUrlRequest,
  removeImportedListing as removeImportedListingRequest,
  refreshImportedListing as refreshImportedListingRequest,
} from '../_data/imported-listings'
import { ImportedListing } from '../_types/imported-listing'
import { useAuth } from './use-auth'
import {
  ImportedListingsContextValue,
  ImportedListingsProviderProps,
} from './imported-listings-context-types'
import { ImportedListingsContext } from './use-imported-listings'

/**
 * Loads + manages the renter's imported listings. Sits below
 * AuthProvider in the App tree so it can read `quickPassUser.id`.
 *
 * @param {ImportedListingsProviderProps} props
 * @returns {JSX.Element}
 */
const ImportedListingsProvider = ({
  children,
}: ImportedListingsProviderProps): JSX.Element => {
  const { quickPassUser, isLoading: isAuthLoading } = useAuth()
  const [listings, setListings] = useState<ImportedListing[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const lastResolvedUserIdReference = useRef<string | null>(null)

  const fetchListings = useCallback(
    async (quickPassUserId: string): Promise<void> => {
      setIsLoading(true)

      try {
        const resolvedListings =
          await getImportedListingsForUser(quickPassUserId)
        setListings(resolvedListings)
      } catch (error) {
        console.error('Failed to fetch imported listings', error)
        setListings([])
      } finally {
        setIsLoading(false)
      }
    },
    [],
  )

  useEffect(() => {
    if (isAuthLoading === true) {
      return
    }

    if (quickPassUser === null) {
      setListings([])
      lastResolvedUserIdReference.current = null
      return
    }

    if (lastResolvedUserIdReference.current === quickPassUser.id) {
      return
    }

    lastResolvedUserIdReference.current = quickPassUser.id
    void fetchListings(quickPassUser.id)
  }, [quickPassUser, isAuthLoading, fetchListings])

  const importFromUrl = useCallback(
    async (url: string): Promise<ImportedListing> => {
      if (quickPassUser === null) {
        throw new Error('Cannot import a listing while signed out.')
      }

      const imported = await importListingFromUrlRequest(quickPassUser.id, url)

      setListings(previous => {
        const filtered = previous.filter(entry => entry.id !== imported.id)
        return [imported, ...filtered]
      })

      return imported
    },
    [quickPassUser],
  )

  const removeListing = useCallback(
    async (listingId: string): Promise<void> => {
      if (quickPassUser === null) {
        return
      }

      await removeImportedListingRequest(quickPassUser.id, listingId)

      setListings(previous => previous.filter(entry => entry.id !== listingId))
    },
    [quickPassUser],
  )

  const refreshListing = useCallback(
    async (listingId: string): Promise<ImportedListing | null> => {
      if (quickPassUser === null) {
        return null
      }

      const refreshed = await refreshImportedListingRequest(
        quickPassUser.id,
        listingId,
      )

      if (refreshed === null) {
        return null
      }

      setListings(previous =>
        previous.map(entry => (entry.id === listingId ? refreshed : entry)),
      )

      return refreshed
    },
    [quickPassUser],
  )

  const refreshAll = useCallback(async (): Promise<void> => {
    if (quickPassUser === null) {
      return
    }

    await fetchListings(quickPassUser.id)
  }, [quickPassUser, fetchListings])

  const value = useMemo<ImportedListingsContextValue>(
    () => ({
      listings,
      isLoading,
      importFromUrl,
      removeListing,
      refreshListing,
      refreshAll,
    }),
    [
      listings,
      isLoading,
      importFromUrl,
      removeListing,
      refreshListing,
      refreshAll,
    ],
  )

  return (
    <ImportedListingsContext.Provider value={value}>
      {children}
    </ImportedListingsContext.Provider>
  )
}

export { ImportedListingsProvider }
